import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createChatConversation,
  deleteChatConversations,
  listChatConversations,
  updateChatConversation,
  updateChatConversationAutoApprove,
  updateChatConversationGateway,
  type ChatContextEntityType,
  type ChatConversationItem,
  type CreateChatConversationInput,
  type ListChatConversationsFilters,
  type LlmGatewayProfile,
} from '../api/chat.client.js';

import { entityListKey, type EntityListKey } from '../../../shared/api/entityQueryKeys.js';

import type { Scope } from '@govard/tenant-core';

export interface UseChatConversationsResult {
  conversations: ChatConversationItem[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isUpdatingGateway: boolean;
  isRenaming: boolean;
  createConversation: (input: CreateChatConversationInput) => Promise<ChatConversationItem>;
  /** Удалить свои диалоги пачкой; чужие сервер молча пропустит. */
  deleteConversations: (conversationIds: string[]) => Promise<void>;
  updateGateway: (
    conversationId: string,
    gatewayProfile: LlmGatewayProfile,
  ) => Promise<ChatConversationItem>;
  updateAutoApprove: (conversationId: string, enabled: boolean) => Promise<ChatConversationItem>;
  renameConversation: (conversationId: string, title: string) => Promise<ChatConversationItem>;
}

export interface UseChatConversationsOptions {
  enabled?: boolean;
}

export function getChatConversationsQueryKey(
  workspaceId: string,
  filters: ListChatConversationsFilters,
): EntityListKey {
  return entityListKey('chat-conversations', workspaceId, { ...filters });
}

export function useChatConversations(
  scope: Scope | null,
  filters: Partial<{ contextEntityType: ChatContextEntityType; contextEntityId: string }> = {},
  options: UseChatConversationsOptions = {},
): UseChatConversationsResult {
  const queryClient = useQueryClient();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryKey = getChatConversationsQueryKey(workspaceId, filters);
  const enabled = scope !== null && (options.enabled ?? true);
  const query = useQuery({
    queryKey,
    queryFn: () => listChatConversations(filters),
    enabled,
    retry: false,
  });
  const createMutation = useMutation({
    mutationFn: createChatConversation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: deleteChatConversations,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const gatewayMutation = useMutation({
    mutationFn: ({
      conversationId,
      gatewayProfile,
    }: {
      conversationId: string;
      gatewayProfile: LlmGatewayProfile;
    }) => updateChatConversationGateway(conversationId, { gatewayProfile }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const autoApproveMutation = useMutation({
    mutationFn: ({ conversationId, enabled }: { conversationId: string; enabled: boolean }) =>
      updateChatConversationAutoApprove(conversationId, enabled),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const renameMutation = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      updateChatConversation(conversationId, { title: title.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    conversations: query.data?.conversations ?? [],
    isLoading: query.isLoading,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdatingGateway: gatewayMutation.isPending,
    isRenaming: renameMutation.isPending,
    createConversation: async (input) => {
      const response = await createMutation.mutateAsync(input);
      return response.conversation;
    },
    deleteConversations: async (conversationIds) => {
      await deleteMutation.mutateAsync(conversationIds);
    },
    updateGateway: async (conversationId, gatewayProfile) => {
      const response = await gatewayMutation.mutateAsync({ conversationId, gatewayProfile });
      return response.conversation;
    },
    updateAutoApprove: async (conversationId, enabled) => {
      const response = await autoApproveMutation.mutateAsync({ conversationId, enabled });
      return response.conversation;
    },
    renameConversation: async (conversationId, title) => {
      const response = await renameMutation.mutateAsync({ conversationId, title });
      return response.conversation;
    },
  };
}
